import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IGetActiveCostsDTO } from "@modules/costs/dtos/IGetActiveCostsDTO";
import { IGetActiveCostsService } from "@modules/costs/models/IGetActiveCostsService";
import AppError from "@utils/AppError";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";

export interface ActiveCostsContextProps {
  rowsActiveCosts: IGetActiveCostsDTO[];
  loadingTable: boolean;
  openDialogDelete: DialogDeleteProps;
  openDialogEdit: DialogEditProps;
  errorLoadingTable: boolean;
  setOpenDialogDelete: React.Dispatch<React.SetStateAction<DialogDeleteProps>>;
  setOpenDialogEdit: React.Dispatch<React.SetStateAction<DialogEditProps>>;
  fetchActiveCosts: () => Promise<void>;
}

export const ActiveCostsContext = React.createContext(
  {} as ActiveCostsContextProps
);

interface DialogDeleteProps {
  open: boolean;
  id: string | null;
}
interface DialogEditProps {
  open: boolean;
  id: string | null;
}

export const ActiveCostsProvider: React.FC = ({ children }) => {
  const iocContext = useIoCContext();
  const { enqueueSnackbar } = useSnackbar();

  const getActiveCostsService = iocContext.serviceContainer.get<
    IGetActiveCostsService
  >(Types.Costs.IGetActiveCostsService);

  const [rowsActiveCosts, setRowsActiveCosts] = useState<IGetActiveCostsDTO[]>(
    []
  );
  const [loadingTable, setLoadingTable] = useState(true);
  const [errorLoadingTable, setErrorLoadingTable] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState<DialogDeleteProps>({
    open: false,
    id: null,
  });
  const [openDialogEdit, setOpenDialogEdit] = useState<DialogEditProps>({
    open: false,
    id: null,
  });

  const fetchActiveCosts = useCallback(async () => {
    try {
      setLoadingTable(true);
      const resp = await getActiveCostsService.execute();
      setRowsActiveCosts(resp);
      setErrorLoadingTable(false);
    } catch (error) {
      if (error instanceof AppError) {
        enqueueSnackbar(error.message, { variant: error.variant });
      } else {
        enqueueSnackbar("Erro ao baixar dados de custos", { variant: "error" });
      }
      setErrorLoadingTable(true);
    } finally {
      setLoadingTable(false);
    }
  }, [enqueueSnackbar, getActiveCostsService]);

  useEffect(() => {
    fetchActiveCosts();
  }, [fetchActiveCosts]);

  return (
    <ActiveCostsContext.Provider
      value={{
        rowsActiveCosts,
        loadingTable,
        openDialogDelete,
        openDialogEdit,
        errorLoadingTable,
        setOpenDialogDelete,
        fetchActiveCosts,
        setOpenDialogEdit,
      }}
    >
      {children}
    </ActiveCostsContext.Provider>
  );
};

export const useActiveCostsContext = () => {
  const context = React.useContext(ActiveCostsContext);
  if (Object.values(context).length === 0) {
    throw new Error(
      "useActiveCostsContext não pode ser utilizado fora de um ActiveCostsProvider"
    );
  }
  return context;
};
